import { action } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Button, Card, CardContent, Grid, GridColumn, Notice, Separator, Spinner, Text, TextField, AnalyticsTrigger } from 'telia-front-react';
import InsuranceStore, { PriceRate } from '../stores/InsuranceStore';
import SsoStore from '../stores/SsoStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import InsuranceCalculatorResult from './InsuranceCalculatorResult';

export interface InsuranceCalculatorProps {
  insuranceStore?: InsuranceStore;
  ssoStore?: SsoStore;
}

export interface InsuranceCalculatorState {
  idCode: string;
  regCode: string;
  submittedFields: string[];
}

const FORM_ID = 'vehicle-insurance-calculator';

@observer
class InsuranceCalculator extends React.Component<InsuranceCalculatorProps, InsuranceCalculatorState> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;

  public constructor(props: InsuranceCalculatorProps) {
    super(props);
    this.state = {
      idCode: this.props.ssoStore.personalCode,
      regCode: '',
      submittedFields: [],
    };
  }

  public componentDidUpdate() {
    if (this.props.ssoStore.personalCode !== this.state.idCode) {
      this.setIdCode();
    }
  }

  @action
  private setIdCode() {
    this.setState({ idCode: this.props.ssoStore.personalCode });
  }

  public render() {
    const translate = this.context.translate;
    const { insuranceStore } = this.props;
    return (
      <Card padding="medium">
        <CardContent>
          <Text className={'text-center'}>
            <h2>{translate('vehicleInsurance.calculator.title')}</h2>
            <p className="text__margin-double">{translate('vehicleInsurance.calculator.description')}</p>
            <AnalyticsTrigger trigger={{ id: `Submit - ${FORM_ID}`, callback: 'onSubmit' }}>
              <form id={FORM_ID} onSubmit={this.handleSearch}>
                <Grid className="text__margin-double" align={['center-xs', 'middle-xs']}>
                  <GridColumn width={['sm-6', 'md-5', 'lg-4', 'xl-3']}>
                    <TextField
                      id="id-code-textfield"
                      label={translate('vehicleInsurance.calculator.personalCode')}
                      value={this.state.idCode}
                      onChange={(val: string) => this.handleIdCodeChange(val)}
                      isInvalid={this.isIdCodeInvalid()}
                    />
                  </GridColumn>
                  <GridColumn width={['sm-6', 'md-5', 'lg-4', 'xl-3']}>
                    <TextField
                      id="vehicle-reg-code-textfield"
                      label={translate('vehicleInsurance.calculator.regNumber')}
                      onChange={(val: string) => this.handleRegCodeChange(val)}
                      value={this.state.regCode}
                      isInvalid={this.isRegCodeInvalid()}
                    />
                  </GridColumn>
                  <GridColumn width={['xs']}>
                    {insuranceStore.state === 'FETCHING' ? (
                      <Spinner />
                    ) : (
                      <Button text={translate('vehicleInsurance.calculator.search')} type="expressive" iconLeft="#search" attributes={{ type: 'submit' }} />
                    )}
                  </GridColumn>
                </Grid>
              </form>
            </AnalyticsTrigger>
            {insuranceStore.state === 'FETCHED' && this.renderSearchResults()}
            <Separator spacing="small" color="invisible" />
            <Grid align={['center-xs', 'middle-xs']}>
              <GridColumn width={['xs']}>
                <AnalyticsTrigger trigger={{ id: 'Link - App store', callback: 'onClick' }}>
                  <Button
                    text="App store"
                    type="text-color"
                    iconLeft="#apple"
                    url={'https://itunes.apple.com/ee/app/msõiduk/id1253952578?mt=8'}
                    target={'_blank'}
                  />
                </AnalyticsTrigger>
              </GridColumn>
              <GridColumn width={['xs']}>
                <AnalyticsTrigger trigger={{ id: 'Link - Google Play', callback: 'onClick' }}>
                  <Button
                    text="Google Play"
                    type="text-color"
                    iconLeft="#google-play"
                    url={'https://play.google.com/store/apps/details?id=com.autlo.teaapp.live'}
                    target={'_blank'}
                  />
                </AnalyticsTrigger>
              </GridColumn>
            </Grid>
          </Text>
        </CardContent>
      </Card>
    );
  }

  private isIdCodeInvalid(submittedFields: string[] = []): boolean {
    return this.propertySubmitted('idCode', submittedFields) && (!this.state.idCode || this.state.idCode.length !== 11);
  }

  private isRegCodeInvalid(submittedFields: string[] = []): boolean {
    return this.propertySubmitted('regCode', submittedFields) && (!this.state.regCode || this.state.regCode.length < 4 || this.state.regCode.length > 7);
  }

  private propertySubmitted(property: string, submittedFields: string[]): boolean {
    if (submittedFields.length) {
      return submittedFields.includes(property);
    }
    return this.state.submittedFields.includes(property);
  }

  private invalidIdAndRegCode(submittedFields: string[]): boolean {
    return this.isIdCodeInvalid(submittedFields) || this.isRegCodeInvalid(submittedFields);
  }

  private handleIdCodeChange = (idCode: string): void => {
    const idCodeIndex = this.state.submittedFields.indexOf('idCode');
    if (idCodeIndex > -1) {
      this.setState((prevState) => {
        const submittedFields = prevState.submittedFields;
        submittedFields.splice(idCodeIndex, 1);
        return { idCode, submittedFields };
      });
    } else {
      this.setState({ idCode });
    }
  };

  private handleRegCodeChange = (regCode: string): void => {
    const regCodeIndex = this.state.submittedFields.indexOf('regCode');
    if (regCodeIndex > -1) {
      this.setState((prevState) => {
        const submittedFields = prevState.submittedFields;
        submittedFields.splice(regCodeIndex, 1);
        return { regCode, submittedFields };
      });
    } else {
      this.setState({ regCode });
    }
  };

  private handleSearch = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const submittedFields = ['idCode', 'regCode'];
    this.setState({ submittedFields });
    if (!this.invalidIdAndRegCode(submittedFields)) {
      this.props.insuranceStore.fetchInsuranceInfos(this.state.idCode, this.state.regCode);
    }
  };

  private renderSearchResults = () => {
    const translate = this.context.translate;
    const {
      insuranceStore: { result, hasResults },
    } = this.props;
    return hasResults ? (
      <Card className="text__margin-double" padding="medium" type="light">
        <CardContent>
          <Grid align={['center-xs']}>
            <InsuranceCalculatorResult
              offerTitle={translate('vehicleInsurance.traffic.label')}
              priceMonth={result.traffic.monthly}
              priceDay={result.traffic.daily}
              totalPrice={this.getMaximumPrice(result.traffic)}
            />
            {result.hull && result.hull.daily && (
              <InsuranceCalculatorResult
                offerTitle={translate('vehicleInsurance.hull.label')}
                priceMonth={result.hull.monthly}
                priceDay={result.hull.daily}
                totalPrice={this.getMaximumPrice(result.hull)}
                gpsNeeded={true}
              />
            )}
          </Grid>
        </CardContent>
      </Card>
    ) : (
      this.renderNoResultsNotice()
    );
  };

  private renderNoResultsNotice = () => {
    const translate = this.context.translate;
    return (
      <Notice type={'error'}>
        <p>{translate('vehicleInsurance.search.noResults')}</p>
      </Notice>
    );
  };

  private getMaximumPrice = (price: PriceRate) => {
    return (+price.daily * 15 + +price.monthly).toFixed(2);
  };
}

export default inject('insuranceStore', 'ssoStore')(InsuranceCalculator);
